import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Button, ClickAwayListener, Popper, Typography } from '@mui/material';
import { ReactNode, useState } from 'react';

/** Maps confirmation icon keys to MUI icon components */
const CONFIRMATION_ICONS: Record<string, React.ComponentType<{ style?: React.CSSProperties }>> = {
  warning: WarningIcon,
  error: ErrorIcon,
  delete: ErrorIcon,
  info: WarningIcon,
};

type ActionConfirmationProps = {
  trigger: (onClick: (event: React.MouseEvent<HTMLElement>) => void) => ReactNode;
  title: ReactNode;
  description: ReactNode;
  onConfirm: () => void;
  confirmText?: ReactNode;
  cancelText?: ReactNode;
  placement?: 'right-start' | 'bottom-start' | 'top-start' | 'left-start';
  icon?: keyof typeof CONFIRMATION_ICONS | string;
  iconColor?: string;
};

export const ActionConfirmation = ({
  trigger,
  title,
  description,
  onConfirm,
  confirmText = 'Accept',
  cancelText = 'Cancel',
  placement = 'right-start',
  icon = 'warning',
  iconColor = '#ff7d55',
}: ActionConfirmationProps) => {
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
    setOpen((previousOpen) => !previousOpen);
  };

  const handleConfirm = () => {
    onConfirm();
    setOpen(false);
  };

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box>
        {trigger(handleClick)}
        <Popper
          open={open}
          anchorEl={anchorEl}
          placement={placement}
          modifiers={[{ name: 'offset', options: { offset: [0, 8] } }]}
        >
          <Box sx={{ border: 0.5, p: 2, bgcolor: 'white', borderRadius: 0.5 }}>
            <Typography
              variant="h5"
              sx={{
                color: 'text.primary',
                display: 'flex',
                gap: '0.5rem',
                alignItems: 'center',
              }}
            >
              {(() => {
                const IconComponent = CONFIRMATION_ICONS[icon] ?? WarningIcon;
                return <IconComponent style={{ color: iconColor }} />;
              })()}
              {title}
            </Typography>
            <Box sx={{ margin: '0.5rem 0' }}>{description}</Box>
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                gap: '0.5rem',
                marginTop: '1rem',
              }}
            >
              <Button
                variant="contained"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  handleConfirm();
                }}
              >
                {confirmText}
              </Button>
              <Button
                variant="outlined"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  setOpen(false);
                }}
              >
                {cancelText}
              </Button>
            </Box>
          </Box>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
};
